import React, { useState, useEffect, useMemo } from 'react';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Slider } from '@/components/ui/slider';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  MousePointerClick, Lock, Monitor, Circle, Type, Check, Plus, Clock3,
  ChevronDown, Sparkles, Zap, Minus, AlignCenter, Eye, ExternalLink, Link2
} from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { VideoFeatures, FinalCountdown } from '@/store/useVideoFeatures';
import { HeroCard } from '@/components/ui/HeroCard';
import { ColorEditorPopover } from '../autoplay/ColorEditorPopover';
import { cn } from '@/lib/utils';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { tw4 } from '@/utils/i18n/wave4';
import {
  getRedirectUrlAutofixSuffix,
  getRedirectUrlState,
  isValidRedirectUrl,
} from '@/lib/redirect-url';

interface FinalCountdownConfigProps {
  features: VideoFeatures;
  setFeature: <K extends keyof VideoFeatures>(key: K, value: VideoFeatures[K]) => void;
}

// Preset values
const STYLE_PRESETS = {
  host: {
    overlayOpacity: 70,
    cardColor: '#ffffff',
    cardOpacity: 100,
    cardBorderRadius: 12,
    circleColor: '#f3f4f6',
    circleTextColor: '#000000',
    circleSize: 'medium' as const,
    buttonColor: '#3b82f6',
    titleColor: '#000000',
    descriptionColor: '#666666',
    overlayColor: '#000000',
  },
  minimal: {
    overlayOpacity: 50,
    cardColor: '#ffffff',
    cardOpacity: 90,
    cardBorderRadius: 16,
    circleColor: '#f8fafc',
    circleTextColor: '#334155',
    circleSize: 'small' as const,
    buttonColor: '#334155',
    titleColor: '#0f172a',
    descriptionColor: '#94a3b8',
    overlayColor: '#000000',
  },
  urgencia: {
    overlayOpacity: 85,
    cardColor: '#1c1917',
    cardOpacity: 100,
    cardBorderRadius: 8,
    circleColor: '#dc2626',
    circleTextColor: '#ffffff',
    circleSize: 'large' as const,
    buttonColor: '#dc2626',
    titleColor: '#ffffff',
    descriptionColor: '#d6d3d1',
    overlayColor: '#0a0a0a',
  },
  clean: {
    overlayOpacity: 60,
    cardColor: '#ffffff',
    cardOpacity: 100,
    cardBorderRadius: 20,
    circleColor: 'transparent',
    circleTextColor: '#0f172a',
    circleSize: 'large' as const,
    buttonColor: '#3b82f6',
    titleColor: '#0f172a',
    descriptionColor: '#64748b',
    overlayColor: '#000000',
  },
};

// Accordion group component
const AccordionGroup: React.FC<{
  icon: React.ReactNode;
  title: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}> = ({ icon, title, defaultOpen = false, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger className={cn(
        "flex items-center justify-between w-full py-3 px-4 rounded-xl bg-white border border-[#E4E7EC] shadow-[0_1px_2px_rgba(0,0,0,0.04)] transition-colors group",
        open && "rounded-b-none border-b-0"
      )}>
        <div className="flex items-center gap-2.5">
          <span className="w-4 h-4 text-muted-foreground">{icon}</span>
          <span className="text-[13px] font-semibold text-[#0F172A]">{title}</span>
        </div>
        <ChevronDown className={cn(
          "w-3.5 h-3.5 text-muted-foreground transition-transform duration-200",
          open && "rotate-180"
        )} />
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="p-4 space-y-4 border border-[#E4E7EC] border-t border-t-[#F1F5F9] rounded-b-xl bg-white shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
          {children}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};

export const FinalCountdownConfig: React.FC<FinalCountdownConfigProps> = ({
  features,
  setFeature,
}) => {
  
  
  const [config, setConfig] = useState<FinalCountdown>({
    enabled: false,
    countdownSeconds: 10,
    redirectUrl: '',
    linkOpenMode: 'sameTab',
    title: tw4('fc.cardTitlePlaceholder'),
    description: tw4('fc.cardDescPlaceholder'),
    showButton: true,
    buttonText: tw4('fc.button.textPlaceholder'),
    buttonAction: 'redirect',
    forcedMode: false,
    overlayColor: '#000000',
    overlayOpacity: 70,
    cardSize: 'medium',
    cardColor: '#ffffff',
    cardOpacity: 100,
    cardBorderRadius: 12,
    fullScreen: false,
    circleColor: '#f3f4f6',
    circleTextColor: '#000000',
    circleSize: 'medium',
    titleColor: '#000000',
    descriptionColor: '#666666',
    buttonColor: '#3b82f6',
    buttonTextColor: '#ffffff',
    buttonBorderRadius: 10,
    animation: 'fadeZoom',
    stylePreset: 'host',
    redirectMode: 'redirect',
  });

  useEffect(() => {
    if (features.finalCountdown) {
      setConfig(prev => ({
        ...prev,
        ...features.finalCountdown,
        stylePreset: features.finalCountdown?.stylePreset || 'host',
        redirectMode: features.finalCountdown?.redirectMode || 'redirect',
        linkOpenMode: features.finalCountdown?.linkOpenMode || 'sameTab',
      }));
    }
  }, [features.finalCountdown]);

  // Estados do campo de URL. `autofix` = digitou sem esquema ("seu-site.com")
  // ou protocol-relative ("//seu-site.com"): é aceito e salvo como https://. `invalid` = esquema proibido
  // (javascript:, data:, ...) ou algo que não vira http(s) — o backend recusa
  // com 400 e o player ignora, então o usuário precisa saber agora.
  const urlState = useMemo((): 'empty' | 'invalid' | 'autofix' | 'valid' | 'none' => {
    if (!config.enabled || config.redirectMode !== 'redirect') return 'none';
    return getRedirectUrlState(config.redirectUrl);
  }, [config.enabled, config.redirectMode, config.redirectUrl]);

  // Mesma regra do player e do preview: só existe botão "Ir Agora" quando há
  // para onde ir. Com modo aviso ou URL inválida o botão não é criado — quem
  // dá saída ao card é o "Fechar e voltar ao vídeo".
  const countdownCanGo = useMemo(
    () => config.redirectMode !== 'warning' && isValidRedirectUrl(config.redirectUrl),
    [config.redirectMode, config.redirectUrl]
  );

  const updateConfig = <K extends keyof FinalCountdown>(key: K, value: FinalCountdown[K]) => {
    const newConfig = { ...config, [key]: value };
    setConfig(newConfig);
    setFeature('finalCountdown', newConfig);
  };

  const applyPreset = (preset: keyof typeof STYLE_PRESETS) => {
    const values = STYLE_PRESETS[preset];
    const newConfig = { ...config, ...values, stylePreset: preset };
    setConfig(newConfig);
    setFeature('finalCountdown', newConfig);
  };


  const timeChips = [3, 5, 10, 15];

  return (
    <div className="space-y-5 px-5 pb-20">
      {/* HeroCard */}
      <HeroCard
        icon={<Clock3 className="w-full h-full" strokeWidth={2} />}
        title={tw4('fc.title')}
        description={tw4('fc.desc')}
        isActive={config.enabled}
        onToggle={(value) => updateConfig('enabled', value)}
      />

      {config.enabled && (
        <Tabs defaultValue="settings" className="w-full">
          <TabsList className="grid w-full max-w-[380px] grid-cols-2 h-10 bg-[#F1F5F9] p-1 rounded-xl">
            <TabsTrigger value="settings" className="h-9 rounded-lg text-[13px] font-medium data-[state=active]:bg-[#4B7BFF] data-[state=active]:text-white data-[state=active]:font-semibold data-[state=active]:shadow-sm text-[#6B7280] transition-all duration-200">{tw4('fc.tab.settings')}</TabsTrigger>
            <TabsTrigger value="style" className="h-9 rounded-lg text-[13px] font-medium data-[state=active]:bg-[#4B7BFF] data-[state=active]:text-white data-[state=active]:font-semibold data-[state=active]:shadow-sm text-[#6B7280] transition-all duration-200">{tw4('fc.tab.style')}</TabsTrigger>
          </TabsList>

          {/* ====================== TAB: CONFIGURAÇÕES ====================== */}
          <TabsContent value="settings" className="space-y-3 mt-4">
            
            {/* Bloco 1 — Essencial (ABERTO) - apenas tempo */}
            <AccordionGroup icon={<Zap className="w-4 h-4" />} title={tw4('fc.essential')} defaultOpen>
              <div className="space-y-2.5">
                <Label className="text-[13px] font-medium">{tw4('fc.timeLabel')}</Label>
                <div className="flex items-center gap-2">
                  <Slider
                    value={[config.countdownSeconds]}
                    onValueChange={([value]) => updateConfig('countdownSeconds', value)}
                    min={3}
                    max={60}
                    step={1}
                    className="flex-1"
                  />
                  <span className="text-[11px] font-medium text-[#4B7BFF] bg-[#4B7BFF]/[0.08] rounded-full px-2 py-0.5 min-w-[44px] text-center">
                    {config.countdownSeconds}s
                  </span>
                </div>
                <div className="flex gap-1.5">
                  {timeChips.map(t => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => updateConfig('countdownSeconds', t)}
                      className={cn(
                        "h-8 rounded-full px-3.5 py-1.5 text-[11px] font-medium transition-colors border",
                        config.countdownSeconds === t
                          ? "border-[#4B7BFF]/40 bg-[#4B7BFF]/[0.08] text-[#4B7BFF] font-semibold"
                          : "border-[#E4E7EC] bg-white text-[#475569] hover:border-[#4B7BFF]/30"
                      )}
                    >
                      {t}s
                    </button>
                  ))}
                </div>
              </div>
            </AccordionGroup>

            {/* Bloco 2 — Redirecionamento (inclui "Ao finalizar") */}
            <AccordionGroup icon={<Link2 className="w-4 h-4" />} title={tw4('fc.redirect')}>
              {/* Ao finalizar — radio buttons */}
              <div className="space-y-2.5">
                <Label className="text-[13px] font-medium">{tw4('fc.onEnd')}</Label>
                <RadioGroup
                  value={config.redirectMode || 'redirect'}
                  onValueChange={(value: 'redirect' | 'warning') => updateConfig('redirectMode', value)}
                  className="space-y-2"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="redirect" id="action-redirect" />
                    <Label htmlFor="action-redirect" className="text-[12px] text-[#374151] cursor-pointer">
                      {tw4('fc.action.redirect')}
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="warning" id="action-warning" />
                    <Label htmlFor="action-warning" className="text-[12px] text-[#374151] cursor-pointer">
                      {tw4('fc.action.warning')}
                    </Label>
                  </div>
                </RadioGroup>
              </div>

              {/* URL + Abertura do link */}
              <div className={cn(
                "space-y-3 transition-opacity duration-200",
                config.redirectMode === 'warning' && "opacity-50 pointer-events-none"
              )}>
                <div className="space-y-2">
                  <Label className="text-[13px] font-medium">{tw4('fc.urlLabel')}</Label>
                  <Input
                    type="url"
                    placeholder={tw4('fc.urlPlaceholder')}
                    value={config.redirectUrl}
                    onChange={(e) => updateConfig('redirectUrl', e.target.value)}
                    disabled={config.redirectMode === 'warning'}
                    className={cn(
                      "h-10",
                      config.redirectMode !== 'warning' && urlState === 'empty' && "border-[#4B7BFF]/30 focus-visible:ring-[#4B7BFF]/20",
                      config.redirectMode !== 'warning' && urlState === 'invalid' && "border-red-200 focus-visible:ring-red-200",
                      config.redirectMode !== 'warning' && urlState === 'valid' && "border-[#4B7BFF]/20"
                    )}
                  />
                  {config.redirectMode !== 'warning' && urlState === 'empty' && (
                    <p className="text-[11px] text-[#64748B]">{tw4('fc.url.empty')}</p>
                  )}
                  {config.redirectMode !== 'warning' && urlState === 'invalid' && (
                    <p className="text-[11px] text-red-400">{tw4('fc.url.invalid')}</p>
                  )}
                  {config.redirectMode !== 'warning' && urlState === 'autofix' && (
                    <p className="text-[11px] text-[#64748B]">
                      {tw4('fc.url.autofix').replace('{url}', getRedirectUrlAutofixSuffix(config.redirectUrl))}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label className="text-[13px] font-medium">{tw4('fc.linkOpen')}</Label>
                  <Select
                    value={config.linkOpenMode || 'sameTab'}
                    onValueChange={(value: 'sameTab' | 'newTab') => updateConfig('linkOpenMode', value)}
                    disabled={config.redirectMode === 'warning'}
                  >
                    <SelectTrigger className="h-10">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sameTab">{tw4('fc.openSame')}</SelectItem>
                      <SelectItem value="newTab">{tw4('fc.openNew')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {config.redirectMode === 'warning' && (
                <p className="text-[11px] text-[#64748B]">
                  {tw4('fc.warningOnly')}
                </p>
              )}
            </AccordionGroup>

            {/* Bloco 4 — Conteúdo do Card */}
            <AccordionGroup icon={<Type className="w-4 h-4" />} title={tw4('fc.cardContent')}>
              <div className="space-y-2">
                <Label className="text-[13px] font-medium">{tw4('fc.cardTitle')}</Label>
                <Input
                  placeholder={tw4('fc.cardTitlePlaceholder')}
                  value={config.title}
                  onChange={(e) => updateConfig('title', e.target.value)}
                  className="h-10"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-[13px] font-medium">{tw4('fc.cardDesc')}</Label>
                <Textarea
                  placeholder={tw4('fc.cardDescPlaceholder')}
                  value={config.description}
                  onChange={(e) => updateConfig('description', e.target.value)}
                  rows={2}
                  className="min-h-[56px] resize-none"
                />
                <p className="text-[11px] text-muted-foreground">{tw4('fc.cardDescHelp')}</p>
              </div>
            </AccordionGroup>

            {/* Bloco 5 — Botão "Ir Agora" */}
            <AccordionGroup icon={<MousePointerClick className="w-4 h-4" />} title={tw4('fc.button')}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <Eye className={cn(
                    "w-4 h-4 transition-colors",
                    config.showButton ? 'text-primary' : 'text-muted-foreground'
                  )} />
                  <span className="text-[13px] font-medium text-[#0F172A]">{tw4('fc.button.show')}</span>
                </div>
                <Switch
                  checked={config.showButton}
                  onCheckedChange={(checked) => updateConfig('showButton', checked)}
                />
              </div>

              {config.showButton && !countdownCanGo && (
                <p className="text-[11px] text-[#B45309] pl-1">
                  {tw4('fc.button.noTarget')}
                </p>
              )}

              {config.showButton && (
                <div className="space-y-3 pl-1">
                  <div className="space-y-2">
                    <Label className="text-[12px] text-muted-foreground">{tw4('fc.button.text')}</Label>
                    <Input
                      placeholder={tw4('fc.button.textPlaceholder')}
                      value={config.buttonText}
                      onChange={(e) => updateConfig('buttonText', e.target.value)}
                      className="h-9"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[12px] text-muted-foreground">{tw4('fc.button.action')}</Label>
                    <Select
                      value={config.buttonAction}
                      onValueChange={(value: 'redirect' | 'newTab') => updateConfig('buttonAction', value)}
                    >
                      <SelectTrigger className="h-9">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="redirect">{tw4('fc.openSame')}</SelectItem>
                        <SelectItem value="newTab">{tw4('fc.openNew')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
            </AccordionGroup>

          </TabsContent>

          {/* ====================== TAB: ESTILO ====================== */}
          <TabsContent value="style" className="space-y-3 mt-4">
            
            {/* Grupo 1 — Aparência (ABERTO) */}
            <AccordionGroup icon={<Monitor className="w-4 h-4" />} title={tw4('fc.appearance')} defaultOpen>
              {/* Modo de exibição */}
              <div className="space-y-2.5">
                <Label className="text-[13px] font-medium">{tw4('fc.displayMode')}</Label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => updateConfig('fullScreen', false)}
                    className={cn(
                      "relative flex flex-col items-center p-3.5 rounded-xl border-2 transition-all duration-200",
                      !config.fullScreen
                        ? "border-[#4B7BFF] bg-[#4B7BFF]/[0.06] shadow-[0_0_0_3px_rgba(75,123,255,0.08)]"
                        : "border-[#E4E7EC] bg-white hover:border-[#4B7BFF]/30"
                    )}
                  >
                    <div className="w-full aspect-video bg-gradient-to-b from-[#2A2A2A] to-[#1A1A1A] rounded-lg mb-2 relative overflow-hidden">
                      <div className="absolute inset-2 bg-black/40 rounded" />
                      <div className="absolute inset-x-4 bottom-2 h-[40%] bg-white/90 rounded" />
                    </div>
                    <span className="text-[11px] font-semibold text-foreground">{tw4('fc.mode.overlay')}</span>
                    {!config.fullScreen && (
                      <div className="absolute top-1.5 right-1.5 w-4 h-4 bg-[#4B7BFF] rounded-full flex items-center justify-center">
                        <Check className="w-2.5 h-2.5 text-white" />
                      </div>
                    )}
                  </button>

                  <button
                    type="button"
                    onClick={() => updateConfig('fullScreen', true)}
                    className={cn(
                      "relative flex flex-col items-center p-3.5 rounded-xl border-2 transition-all duration-200",
                      config.fullScreen
                        ? "border-[#4B7BFF] bg-[#4B7BFF]/[0.06] shadow-[0_0_0_3px_rgba(75,123,255,0.08)]"
                        : "border-[#E4E7EC] bg-white hover:border-[#4B7BFF]/30"
                    )}
                  >
                    <div className="w-full aspect-video bg-[#0A0A0A] rounded-lg mb-2 relative overflow-hidden">
                      <div className="absolute inset-x-4 inset-y-2 bg-white/90 rounded" />
                    </div>
                    <span className="text-[11px] font-semibold text-foreground">{tw4('fc.mode.fullscreen')}</span>
                    {config.fullScreen && (
                      <div className="absolute top-1.5 right-1.5 w-4 h-4 bg-[#4B7BFF] rounded-full flex items-center justify-center">
                        <Check className="w-2.5 h-2.5 text-white" />
                      </div>
                    )}
                  </button>
                </div>
              </div>

              {/* Preset de estilo */}
              <div className="space-y-2.5">
                <Label className="text-[13px] font-medium">{tw4('fc.preset')}</Label>
                <div className="grid grid-cols-2 gap-2.5">
                  {([
                    { key: 'host', label: tw4('fc.preset.host'), sub: tw4('fc.preset.host.sub'), icon: <Sparkles className="w-3.5 h-3.5" /> },
                    { key: 'minimal', label: tw4('fc.preset.minimal'), sub: tw4('fc.preset.minimal.sub'), icon: <Eye className="w-3.5 h-3.5" /> },
                  ] as const).map(preset => (
                    <button
                      key={preset.key}
                      type="button"
                      onClick={() => applyPreset(preset.key)}
                      className={cn(
                        "relative flex flex-col items-start p-3 rounded-xl border-2 transition-all duration-200 text-left",
                        config.stylePreset === preset.key
                          ? "border-[#4B7BFF] bg-[#4B7BFF]/[0.06] shadow-[0_0_0_3px_rgba(75,123,255,0.08)]"
                          : "border-[#E4E7EC] bg-white hover:border-[#4B7BFF]/30"
                      )}
                    >
                      <div className="flex items-center gap-1.5 mb-0.5">
                        <span className="text-muted-foreground">{preset.icon}</span>
                        <span className="text-[11px] font-semibold text-foreground">{preset.label}</span>
                      </div>
                      <span className="text-[10px] text-muted-foreground">{preset.sub}</span>
                      {config.stylePreset === preset.key && (
                        <div className="absolute top-1.5 right-1.5 w-3.5 h-3.5 bg-[#4B7BFF] rounded-full flex items-center justify-center">
                          <Check className="w-2 h-2 text-white" />
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </AccordionGroup>

            {/* Grupo 2 — Ajustes rápidos (FECHADO) */}
            <AccordionGroup icon={<Sparkles className="w-4 h-4" />} title={tw4('fc.quick')}>
              {/* Opacidade do fundo */}
              <div className="space-y-2">
                <Label className="text-[12px] text-muted-foreground">{tw4('fc.quick.opacity')}</Label>
                <div className="flex items-center gap-2">
                  <Slider
                    value={[config.overlayOpacity]}
                    onValueChange={([value]) => updateConfig('overlayOpacity', value)}
                    min={0}
                    max={100}
                    step={5}
                    className="flex-1"
                  />
                  <span className="text-[11px] font-medium text-[#4B7BFF] bg-[#4B7BFF]/[0.08] rounded-full px-2 py-0.5 min-w-[44px] text-center">
                    {config.overlayOpacity}%
                  </span>
                </div>
              </div>

              {/* Raio do card */}
              <div className="space-y-2">
                <Label className="text-[12px] text-muted-foreground">{tw4('fc.quick.radius')}</Label>
                <div className="flex items-center gap-2">
                  <Slider
                    value={[config.cardBorderRadius]}
                    onValueChange={([value]) => updateConfig('cardBorderRadius', value)}
                    min={0}
                    max={30}
                    step={2}
                    className="flex-1"
                  />
                  <span className="text-[11px] font-medium text-[#4B7BFF] bg-[#4B7BFF]/[0.08] rounded-full px-2 py-0.5 min-w-[44px] text-center">
                    {config.cardBorderRadius}px
                  </span>
                </div>
              </div>

              {/* Cor principal (accent) */}
              <div className="space-y-2">
                <Label className="text-[12px] text-muted-foreground">{tw4('fc.quick.mainColor')}</Label>
                <div className="flex items-center gap-2">
                  <ColorEditorPopover
                    colorName={tw4('fc.quick.mainColorName')}
                    currentColor={config.buttonColor}
                    onSave={(newColor) => updateConfig('buttonColor', newColor)}
                  >
                    <button
                      type="button"
                      className="w-8 h-8 rounded-full border-2 border-background shadow-md hover:scale-110 transition-transform cursor-pointer"
                      style={{ backgroundColor: config.buttonColor }}
                    />
                  </ColorEditorPopover>
                  <span className="text-[11px] text-muted-foreground">{tw4('fc.quick.mainColorHelp')}</span>
                </div>
              </div>

              {/* Cor do fundo */}
              <div className="space-y-2">
                <Label className="text-[12px] text-muted-foreground">{tw4('fc.quick.bgColor')}</Label>
                <div className="flex items-center gap-2">
                  {['#000000', '#1A1A1A', '#0A1628', '#1E3A5F'].map((color) => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => updateConfig('overlayColor', color)}
                      className={cn(
                        "w-7 h-7 rounded-full border-2 transition-all hover:scale-110",
                        config.overlayColor === color
                          ? "border-[#4B7BFF] ring-2 ring-[#4B7BFF]/20"
                          : "border-[#E4E7EC]"
                      )}
                      style={{ backgroundColor: color }}
                    />
                  ))}
                  <ColorEditorPopover
                    colorName={tw4('fc.quick.customColor')}
                    currentColor={config.overlayColor}
                    onSave={(newColor) => updateConfig('overlayColor', newColor)}
                  >
                    <button
                      type="button"
                      className="w-7 h-7 rounded-full border-2 border-dashed border-muted-foreground/30 flex items-center justify-center hover:border-primary transition-colors"
                    >
                      <Plus className="w-3 h-3 text-muted-foreground" />
                    </button>
                  </ColorEditorPopover>
                </div>
              </div>
            </AccordionGroup>

            {/* Grupo 3 — Contagem (FECHADO) */}
            <AccordionGroup icon={<Circle className="w-4 h-4" />} title={tw4('fc.count')}>
              <div className="grid grid-cols-3 gap-3">
                <div className="flex flex-col items-center gap-1.5">
                  <span className="text-[11px] text-muted-foreground font-medium">{tw4('fc.count.circle')}</span>
                  <ColorEditorPopover
                    colorName={tw4('fc.count.circle')}
                    currentColor={config.circleColor}
                    onSave={(newColor) => updateConfig('circleColor', newColor)}
                  >
                    <button
                      type="button"
                      className="w-9 h-9 rounded-full border-2 border-background shadow-md hover:scale-110 transition-transform cursor-pointer"
                      style={{ backgroundColor: config.circleColor }}
                    />
                  </ColorEditorPopover>
                </div>
                <div className="flex flex-col items-center gap-1.5">
                  <span className="text-[11px] text-muted-foreground font-medium">{tw4('fc.count.number')}</span>
                  <ColorEditorPopover
                    colorName={tw4('fc.count.number')}
                    currentColor={config.circleTextColor}
                    onSave={(newColor) => updateConfig('circleTextColor', newColor)}
                  >
                    <button
                      type="button"
                      className="w-9 h-9 rounded-full border-2 border-background shadow-md hover:scale-110 transition-transform cursor-pointer"
                      style={{ backgroundColor: config.circleTextColor }}
                    />
                  </ColorEditorPopover>
                </div>
                <div className="flex flex-col items-center gap-1.5">
                  <span className="text-[11px] text-muted-foreground font-medium">{tw4('fc.count.size')}</span>
                  <Select
                    value={config.circleSize}
                    onValueChange={(value: 'small' | 'medium' | 'large') => updateConfig('circleSize', value)}
                  >
                    <SelectTrigger className="h-9 w-[70px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="small">{tw4('fc.size.s')}</SelectItem>
                      <SelectItem value="medium">{tw4('fc.size.m')}</SelectItem>
                      <SelectItem value="large">{tw4('fc.size.l')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </AccordionGroup>

            {/* Grupo 4 — Tipografia (FECHADO) */}
            <AccordionGroup icon={<Type className="w-4 h-4" />} title={tw4('fc.typography')}>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex flex-col items-center gap-1.5">
                  <span className="text-[11px] text-muted-foreground font-medium">{tw4('fc.typo.title')}</span>
                  <ColorEditorPopover
                    colorName={tw4('fc.typo.title')}
                    currentColor={config.titleColor}
                    onSave={(newColor) => updateConfig('titleColor', newColor)}
                  >
                    <button
                      type="button"
                      className="w-9 h-9 rounded-full border-2 border-background shadow-md hover:scale-110 transition-transform cursor-pointer"
                      style={{ backgroundColor: config.titleColor }}
                    />
                  </ColorEditorPopover>
                </div>
                <div className="flex flex-col items-center gap-1.5">
                  <span className="text-[11px] text-muted-foreground font-medium">{tw4('fc.typo.desc')}</span>
                  <ColorEditorPopover
                    colorName={tw4('fc.typo.desc')}
                    currentColor={config.descriptionColor}
                    onSave={(newColor) => updateConfig('descriptionColor', newColor)}
                  >
                    <button
                      type="button"
                      className="w-9 h-9 rounded-full border-2 border-background shadow-md hover:scale-110 transition-transform cursor-pointer"
                      style={{ backgroundColor: config.descriptionColor }}
                    />
                  </ColorEditorPopover>
                </div>
              </div>
            </AccordionGroup>

            {/* Grupo 5 — Botão (FECHADO, só aparece se showButton ON) */}
            {config.showButton && (
              <AccordionGroup icon={<MousePointerClick className="w-4 h-4" />} title={tw4('fc.btnGroup')}>
                <div className="grid grid-cols-3 gap-3">
                  <div className="flex flex-col items-center gap-1.5">
                    <span className="text-[11px] text-muted-foreground font-medium">{tw4('fc.btn.color')}</span>
                    <ColorEditorPopover
                      colorName={tw4('fc.btn.color')}
                      currentColor={config.buttonColor}
                      onSave={(newColor) => updateConfig('buttonColor', newColor)}
                    >
                      <button
                        type="button"
                        className="w-9 h-9 rounded-full border-2 border-background shadow-md hover:scale-110 transition-transform cursor-pointer"
                        style={{ backgroundColor: config.buttonColor }}
                      />
                    </ColorEditorPopover>
                  </div>
                  <div className="flex flex-col items-center gap-1.5">
                    <span className="text-[11px] text-muted-foreground font-medium">{tw4('fc.btn.text')}</span>
                    <ColorEditorPopover
                      colorName={tw4('fc.btn.text')}
                      currentColor={config.buttonTextColor}
                      onSave={(newColor) => updateConfig('buttonTextColor', newColor)}
                    >
                      <button
                        type="button"
                        className="w-9 h-9 rounded-full border-2 border-background shadow-md hover:scale-110 transition-transform cursor-pointer"
                        style={{ backgroundColor: config.buttonTextColor }}
                      />
                    </ColorEditorPopover>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-[11px] text-muted-foreground font-medium">{tw4('fc.btn.radius')}</Label>
                    <div className="flex items-center gap-2">
                      <Slider
                        value={[config.buttonBorderRadius]}
                        onValueChange={([value]) => updateConfig('buttonBorderRadius', value)}
                        min={0}
                        max={40}
                        step={2}
                        className="flex-1"
                      />
                      <span className="text-[10px] font-medium text-muted-foreground w-[32px] text-right">
                        {config.buttonBorderRadius}px
                      </span>
                    </div>
                  </div>
                </div>
              </AccordionGroup>
            )}

          </TabsContent>
        </Tabs>
      )}
    </div>
  );
};
